import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import StructuredData from '@/components/base/StructuredData';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { siteConfig } from '@/mocks/site';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'profile');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory),
    [activeCategory]
  );

  const filteredArticles = useMemo(
    () => articles.filter((a) => a.category === activeCategory),
    [activeCategory]
  );

  const categoryTitle = currentCategory?.name || '分类';
  const categoryDesc = currentCategory?.description || '';

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `冲田杏梨${categoryTitle}有哪些内容？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: categoryDesc,
        },
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={`冲田杏梨${categoryTitle} - ${categoryDesc} | ${siteConfig.name}`}
        description={`冲田杏梨${categoryTitle}${categoryDesc}。汇集最全的冲田杏梨相关${categoryTitle}资讯，为粉丝提供一站式信息参考。`}
        keywords={`冲田杏梨${categoryTitle},${categoryTitle},冲田杏梨,${siteConfig.keywords}`}
        canonicalUrl={`${siteConfig.url}/category/${activeCategory}`}
        ogImage="https://readdy.ai/api/search-image?query=Elegant%20Japanese%20magazine%20layout%20with%20warm%20golden%20and%20cream%20tones%2C%20minimalist%20editorial%20design%2C%20soft%20lighting%2C%20sophisticated%20aesthetic&width=1200&height=630&seq=anri-category-og&orientation=landscape"
      />
      <StructuredData data={faqSchema} />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryTitle },
            ]}
          />

          <section className="py-8 md:py-10 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-[family-name:var(--font-heading)]">
                  冲田杏梨{categoryTitle}
                </h1>
                <p className="mt-2 text-sm text-foreground-500">{categoryDesc}</p>
              </div>

              {/* Category Tabs */}
              <div className="flex flex-nowrap overflow-x-auto gap-2 pb-2 mb-8 scrollbar-hide">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer flex items-center gap-1.5 ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                    }`}
                  >
                    <i className={`${cat.icon} text-xs`}></i>
                    {cat.name}
                    <span className={`text-xs ml-1 ${
                      activeCategory === cat.slug ? 'text-background-50/70' : 'text-foreground-400'
                    }`}>
                      ({cat.count})
                    </span>
                  </Link>
                ))}
              </div>

              {filteredArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {filteredArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-file-list-3-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容</p>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}